export type Locale = "en" | "vi";

const DEFAULT_LOCALE: Locale = "en";

const translations = {
  en: {
    "shell.brandTitle": "Assistant Admin",
    "shell.brandSubtitle": "Control Workspace",
    "shell.health": "Health",
    "shell.status.ok": "OK",
    "shell.status.offline": "Offline",
    "shell.resources": "Resources",
    "shell.docs": "Docs",
    "shell.docsTitle": "Docs (opens in new tab)",
    "shell.expandSidebar": "Expand sidebar",
    "shell.collapseSidebar": "Collapse sidebar",
    "shell.languageToggle": "Switch language",
    "shell.language.english": "English",
    "shell.language.vietnamese": "Vietnamese",
    "nav.group.chat": "Chat",
    "nav.group.control": "Control",
    "nav.group.agent": "Agent",
    "nav.group.settings": "Settings",
    "theme.group": "Theme",
    "theme.system": "System",
    "theme.light": "Light",
    "theme.dark": "Dark",
    "theme.systemAria": "System theme",
    "theme.lightAria": "Light theme",
    "theme.darkAria": "Dark theme",
    "tab.agents.title": "Agents",
    "tab.agents.subtitle": "Manage agent workspaces, tools, and identities.",
    "tab.overview.title": "Overview",
    "tab.overview.subtitle": "Gateway status, entry points, and a fast health read.",
    "tab.channels.title": "Channels",
    "tab.channels.subtitle": "Manage channels and settings.",
    "tab.instances.title": "Instances",
    "tab.instances.subtitle": "Presence beacons from connected clients and nodes.",
    "tab.sessions.title": "Sessions",
    "tab.sessions.subtitle": "Inspect active sessions and adjust per-session defaults.",
    "tab.usage.title": "Usage",
    "tab.usage.subtitle": "",
    "tab.cron.title": "Cron Jobs",
    "tab.cron.subtitle": "Schedule wakeups and recurring agent runs.",
    "tab.skills.title": "Skills",
    "tab.skills.subtitle": "Manage skill availability and API key injection.",
    "tab.nodes.title": "Nodes",
    "tab.nodes.subtitle": "Paired devices, capabilities, and command exposure.",
    "tab.kb.title": "KB",
    "tab.kb.subtitle": "Knowledge folders and markdown files indexed for memory search.",
    "tab.chat.title": "Chat",
    "tab.chat.subtitle": "Direct gateway chat session for quick interventions.",
    "tab.config.title": "Config",
    "tab.config.subtitle": "Edit ~/.openclaw/openclaw.json safely.",
    "tab.debug.title": "Debug",
    "tab.debug.subtitle": "Gateway snapshots, events, and manual RPC calls.",
    "tab.logs.title": "Logs",
    "tab.logs.subtitle": "Live tail of the gateway file logs.",
    "chat.disconnected": "Disconnected from gateway.",
    "chat.refresh": "Refresh chat data",
    "chat.disabledDuringOnboarding": "Disabled during onboarding",
    "chat.toggleThinking": "Toggle assistant thinking/working output",
    "chat.toggleFocus": "Toggle focus mode (hide sidebar + page header)",
    "loader.badge": "Assistant Admin",
    "loader.title": "Loading control workspace",
    "loader.subtitle": "Booting the gateway dashboard inside the new React host.",
  },
  vi: {
    "shell.brandTitle": "Assistant Admin",
    "shell.brandSubtitle": "Trung tâm điều khiển",
    "shell.health": "Trạng thái",
    "shell.status.ok": "OK",
    "shell.status.offline": "Ngoại tuyến",
    "shell.resources": "Tài nguyên",
    "shell.docs": "Tài liệu",
    "shell.docsTitle": "Tài liệu (mở tab mới)",
    "shell.expandSidebar": "Mở rộng menu",
    "shell.collapseSidebar": "Thu gọn menu",
    "shell.languageToggle": "Đổi ngôn ngữ",
    "shell.language.english": "English",
    "shell.language.vietnamese": "Tiếng Việt",
    "nav.group.chat": "Trò chuyện",
    "nav.group.control": "Điều khiển",
    "nav.group.agent": "Agent",
    "nav.group.settings": "Cài đặt",
    "theme.group": "Giao diện",
    "theme.system": "Hệ thống",
    "theme.light": "Sáng",
    "theme.dark": "Tối",
    "theme.systemAria": "Chế độ hệ thống",
    "theme.lightAria": "Chế độ sáng",
    "theme.darkAria": "Chế độ tối",
    "tab.agents.title": "Agents",
    "tab.agents.subtitle": "Quản lý workspace, công cụ và định danh agent.",
    "tab.overview.title": "Tổng quan",
    "tab.overview.subtitle": "Trạng thái gateway, điểm vào và tổng quan sức khỏe nhanh.",
    "tab.channels.title": "Kênh",
    "tab.channels.subtitle": "Quản lý kênh và cấu hình.",
    "tab.instances.title": "Instances",
    "tab.instances.subtitle": "Tín hiệu hiện diện từ client và node đang kết nối.",
    "tab.sessions.title": "Phiên",
    "tab.sessions.subtitle": "Xem session đang hoạt động và điều chỉnh mặc định theo session.",
    "tab.usage.title": "Sử dụng",
    "tab.usage.subtitle": "",
    "tab.cron.title": "Tác vụ định kỳ",
    "tab.cron.subtitle": "Lên lịch wakeup và chạy agent định kỳ.",
    "tab.skills.title": "Kỹ năng",
    "tab.skills.subtitle": "Quản lý skill và API key injection.",
    "tab.nodes.title": "Nodes",
    "tab.nodes.subtitle": "Thiết bị đã pair, khả năng và quyền chạy lệnh.",
    "tab.kb.title": "Kho tri thức",
    "tab.kb.subtitle": "Thư mục tri thức và file markdown được đánh chỉ mục để tìm kiếm bộ nhớ.",
    "tab.chat.title": "Trò chuyện",
    "tab.chat.subtitle": "Phiên chat trực tiếp với gateway để can thiệp nhanh.",
    "tab.config.title": "Cấu hình",
    "tab.config.subtitle": "Chỉnh sửa ~/.openclaw/openclaw.json an toàn.",
    "tab.debug.title": "Gỡ lỗi",
    "tab.debug.subtitle": "Snapshot gateway, sự kiện và gọi RPC thủ công.",
    "tab.logs.title": "Nhật ký",
    "tab.logs.subtitle": "Theo dõi file log gateway theo thời gian thực.",
    "chat.disconnected": "Đã ngắt kết nối tới gateway.",
    "chat.refresh": "Làm mới dữ liệu chat",
    "chat.disabledDuringOnboarding": "Bị vô hiệu hóa trong onboarding",
    "chat.toggleThinking": "Bật tắt phần suy nghĩ và xử lý của trợ lý",
    "chat.toggleFocus": "Bật tắt chế độ tập trung (ẩn menu và tiêu đề)",
    "loader.badge": "Assistant Admin",
    "loader.title": "Đang tải trung tâm điều khiển",
    "loader.subtitle": "Đang khởi động bảng điều khiển gateway trong React host mới.",
  },
} as const;

export type TranslationKey = keyof (typeof translations)["en"];

const exactLiteralVi: Record<string, string> = {
  Chat: "Trò chuyện",
  Control: "Điều khiển",
  Agent: "Agent",
  Settings: "Cài đặt",
  Resources: "Tài nguyên",
  Overview: "Tổng quan",
  Channels: "Kênh",
  Instances: "Instances",
  Sessions: "Phiên",
  Usage: "Sử dụng",
  "Cron Jobs": "Tác vụ định kỳ",
  Skills: "Kỹ năng",
  Nodes: "Nodes",
  KB: "Kho tri thức",
  Config: "Cấu hình",
  Debug: "Gỡ lỗi",
  Logs: "Nhật ký",
  Docs: "Tài liệu",
  Health: "Trạng thái",
  Offline: "Ngoại tuyến",
  "Disconnected from gateway.": "Đã ngắt kết nối tới gateway.",
  "Disabled during onboarding": "Bị vô hiệu hóa trong onboarding",
  "Direct gateway chat session for quick interventions.":
    "Phiên chat trực tiếp với gateway để can thiệp nhanh.",
  "Refresh chat data": "Làm mới dữ liệu chat",
  "Toggle assistant thinking/working output": "Bật tắt phần suy nghĩ và xử lý của trợ lý",
  "Toggle focus mode (hide sidebar + page header)": "Bật tắt chế độ tập trung (ẩn menu và tiêu đề)",
  "Connect to the gateway to start chatting…": "Kết nối tới gateway để bắt đầu trò chuyện…",
  "New session": "Phiên mới",
  "Gateway Access": "Truy cập gateway",
  "Where the dashboard connects and how it authenticates.":
    "Nơi dashboard kết nối và cách xác thực.",
  "WebSocket URL": "URL WebSocket",
  "Gateway Token": "Token gateway",
  "Gateway access token": "Token truy cập gateway",
  "Password (not stored)": "Mật khẩu (không lưu)",
  "system or shared password": "mật khẩu hệ thống hoặc dùng chung",
  "Default Session Key": "Session key mặc định",
  Connect: "Kết nối",
  Refresh: "Làm mới",
  "Click Connect to apply connection changes.":
    "Nhấn Kết nối để áp dụng thay đổi kết nối.",
  Snapshot: "Snapshot",
  "Latest gateway handshake information.": "Thông tin bắt tay gateway mới nhất.",
  Status: "Trạng thái",
  Connected: "Đã kết nối",
  Disconnected: "Đã ngắt kết nối",
  Uptime: "Thời gian hoạt động",
  "Tick Interval": "Chu kỳ tick",
  "Last Channels Refresh": "Lần làm mới kênh gần nhất",
  "Use Channels to link WhatsApp, Telegram, Discord, Signal, or iMessage.":
    "Dùng mục Kênh để liên kết WhatsApp, Telegram, Discord, Signal hoặc iMessage.",
  "This gateway requires auth. Add a token or password, then click Connect.":
    "Gateway này yêu cầu xác thực. Thêm token hoặc mật khẩu rồi nhấn Kết nối.",
  "Auth failed. Update the token or password in Control UI settings, then click Connect.":
    "Xác thực thất bại. Cập nhật token hoặc mật khẩu trong cài đặt giao diện rồi nhấn Kết nối.",
  "This page is HTTP, so the browser blocks device identity. Use HTTPS (Tailscale Serve) or open":
    "Trang này đang là HTTP nên trình duyệt chặn device identity. Hãy dùng HTTPS (Tailscale Serve) hoặc mở",
  "If you must stay on HTTP, set":
    "Nếu bắt buộc dùng HTTP, hãy đặt",
  Notes: "Ghi chú",
  "Quick reminders for remote control setups.":
    "Các ghi chú nhanh cho cấu hình điều khiển từ xa.",
  "Tailscale serve": "Tailscale serve",
  "Session hygiene": "Vệ sinh session",
  "Cron reminders": "Lưu ý cron",
  "Prefer serve mode to keep the gateway on loopback with tailnet auth.":
    "Nên dùng serve mode để giữ gateway trên loopback với xác thực tailnet.",
  "Use /new or sessions.patch to reset context.":
    "Dùng /new hoặc sessions.patch để reset context.",
  "Use isolated sessions for recurring runs.":
    "Dùng session cô lập cho các lần chạy định kỳ.",
  Agents: "Agents",
  "Manage agent workspaces, tools, and identities.":
    "Quản lý workspace, công cụ và định danh agent.",
  "No agents found.": "Không tìm thấy agent nào.",
  "Select an agent": "Chọn agent",
  "Pick an agent to inspect its workspace and tools.":
    "Chọn một agent để xem workspace và công cụ.",
  "Manage channels and settings.": "Quản lý kênh và cấu hình.",
  "Channel health": "Sức khỏe kênh",
  "Channel status snapshots from the gateway.": "Snapshot trạng thái kênh từ gateway.",
  "No snapshot yet.": "Chưa có snapshot.",
  Scheduler: "Bộ lập lịch",
  "Gateway-owned cron scheduler status.": "Trạng thái bộ lập lịch cron do gateway quản lý.",
  Enabled: "Bật",
  Disabled: "Tắt",
  Jobs: "Tác vụ",
  "New Job": "Tác vụ mới",
  "Create a scheduled wakeup or agent run.": "Tạo lịch wakeup hoặc chạy agent.",
  Name: "Tên",
  Description: "Mô tả",
  "Agent ID": "ID agent",
  Schedule: "Lịch",
  Every: "Mỗi",
  At: "Tại",
  Cron: "Cron",
  Session: "Phiên",
  "Wake mode": "Chế độ đánh thức",
  Payload: "Nội dung",
  "System event": "Sự kiện hệ thống",
  "Agent turn": "Lượt agent",
  "System text": "Nội dung hệ thống",
  "Agent message": "Tin nhắn agent",
  Delivery: "Cách gửi",
  "None (internal)": "Không (nội bộ)",
  "Timeout (seconds)": "Timeout (giây)",
  Channel: "Kênh",
  Filter: "Bộ lọc",
  "Search skills": "Tìm skill",
  "Bundled, managed, and workspace skills.": "Skill tích hợp, cài đặt và trong workspace.",
  "No skills found.": "Không tìm thấy skill nào.",
  Devices: "Thiết bị",
  "Pairing requests + role tokens.": "Yêu cầu pair + token vai trò.",
  Pending: "Đang chờ",
  Paired: "Đã pair",
  "No paired devices.": "Chưa có thiết bị nào được pair.",
  Approve: "Chấp nhận",
  Reject: "Từ chối",
  Rotate: "Xoay mới",
  Revoke: "Thu hồi",
  "Paired devices and live links.": "Thiết bị đã pair và liên kết đang sống.",
  "No nodes found.": "Không tìm thấy node nào.",
  "Knowledge folders and markdown files indexed for memory search.":
    "Thư mục tri thức và file markdown được đánh chỉ mục để tìm kiếm bộ nhớ.",
  "Inspect active sessions and adjust per-session defaults.":
    "Xem session đang hoạt động và điều chỉnh mặc định theo session.",
  "Active session keys and per-session overrides.":
    "Session key đang hoạt động và override theo session.",
  "Active within (minutes)": "Hoạt động trong (phút)",
  Limit: "Giới hạn",
  "Include global": "Bao gồm global",
  "Include unknown": "Bao gồm unknown",
  "No sessions found.": "Không tìm thấy session nào.",
  "Edit ~/.openclaw/openclaw.json safely.": "Chỉnh sửa ~/.openclaw/openclaw.json an toàn.",
  "Gateway snapshots, events, and manual RPC calls.":
    "Snapshot gateway, sự kiện và gọi RPC thủ công.",
  "Live tail of the gateway file logs.": "Theo dõi file log gateway theo thời gian thực.",
  "Loading control workspace": "Đang tải trung tâm điều khiển",
  "Booting the gateway dashboard inside the new React host.":
    "Đang khởi động bảng điều khiển gateway trong React host mới.",
  "Manage skill availability and API key injection.":
    "Quản lý độ sẵn sàng của skill và API key injection.",
  "Paired devices, capabilities, and command exposure.":
    "Thiết bị đã pair, khả năng và quyền chạy lệnh.",
  "Schedule wakeups and recurring agent runs.": "Lên lịch wakeup và chạy agent định kỳ.",
  "OpenClaw": "Assistant Admin",
  OPENCLAW: "Assistant Admin",
  "Gateway Dashboard": "Trung tâm điều khiển",
  "OPENCLAW_GATEWAY_TOKEN": "Token truy cập gateway",
  "Docs: Control UI auth": "Tài liệu: Xác thực giao diện",
  "Docs: Tailscale Serve": "Tài liệu: Tailscale Serve",
  "Docs: Insecure HTTP": "Tài liệu: HTTP không an toàn",
  "Control UI auth docs (opens in new tab)": "Tài liệu xác thực giao diện (mở tab mới)",
  "Tailscale Serve docs (opens in new tab)": "Tài liệu Tailscale Serve (mở tab mới)",
  "Insecure HTTP docs (opens in new tab)": "Tài liệu HTTP không an toàn (mở tab mới)",
  "Docs (opens in new tab)": "Tài liệu (mở tab mới)",
  "System theme": "Chế độ hệ thống",
  "Light theme": "Chế độ sáng",
  "Dark theme": "Chế độ tối",
  System: "Hệ thống",
  Light: "Sáng",
  Dark: "Tối",
  "Expand sidebar": "Mở rộng menu",
  "Collapse sidebar": "Thu gọn menu",
  "Loading…": "Đang tải…",
  "Loading...": "Đang tải...",
  "Refreshing…": "Đang làm mới…",
  "Refreshing...": "Đang làm mới...",
  "No logs yet.": "Chưa có log nào.",
};

type LiteralRule = {
  test: RegExp;
  translate: (match: RegExpExecArray) => string;
};

const literalRules: Record<Locale, LiteralRule[]> = {
  en: [],
  vi: [
    {
      test: /^(\d+) configured\.$/,
      translate: (match) => `${match[1]} mục đã cấu hình.`,
    },
    {
      test: /^Store: (.+)$/,
      translate: (match) => `Kho lưu trữ: ${match[1]}`,
    },
    {
      test: /^Current \((.+)\)$/,
      translate: (match) => `Hiện tại (${match[1]})`,
    },
    {
      test: /^role: (.+)$/i,
      translate: (match) => `vai trò: ${match[1]}`,
    },
    {
      test: /^roles: (.+)$/i,
      translate: (match) => `vai trò: ${match[1]}`,
    },
    {
      test: /^scopes: (.+)$/i,
      translate: (match) => `phạm vi: ${match[1]}`,
    },
    {
      test: /^Missing: (.+)$/,
      translate: (match) => `Thiếu: ${match[1]}`,
    },
    {
      test: /^Reason: (.+)$/,
      translate: (match) => `Lý do: ${match[1]}`,
    },
    {
      test: /^(\d+)m ago$/,
      translate: (match) => `${match[1]} phút trước`,
    },
    {
      test: /^(\d+)h ago$/,
      translate: (match) => `${match[1]} giờ trước`,
    },
    {
      test: /^(\d+)d ago$/,
      translate: (match) => `${match[1]} ngày trước`,
    },
    {
      test: /^in (\d+)m$/,
      translate: (match) => `trong ${match[1]} phút`,
    },
    {
      test: /^in (\d+)h$/,
      translate: (match) => `trong ${match[1]} giờ`,
    },
    {
      test: /^in (\d+)d$/,
      translate: (match) => `trong ${match[1]} ngày`,
    },
  ],
};

const textNodeState = new WeakMap<Text, { source: string; lastApplied: string }>();
const attrState = new WeakMap<Element, Map<string, { source: string; lastApplied: string }>>();
const LOCALIZED_ATTRIBUTES = ["title", "placeholder", "aria-label", "value"] as const;

export function resolveInitialLocale(raw?: string | null): Locale {
  const candidate = (raw ?? "").trim().toLowerCase();
  if (candidate === "vi" || candidate.startsWith("vi-")) {
    return "vi";
  }
  if (candidate === "en" || candidate.startsWith("en-")) {
    return "en";
  }
  if (typeof navigator !== "undefined") {
    const language = navigator.language?.trim().toLowerCase() ?? "";
    if (language.startsWith("vi")) {
      return "vi";
    }
  }
  return DEFAULT_LOCALE;
}

export function t(locale: Locale, key: TranslationKey, vars?: Record<string, string | number>) {
  let value = String(translations[locale][key] ?? translations.en[key] ?? key);
  if (!vars) {
    return value;
  }
  for (const [name, replacement] of Object.entries(vars)) {
    value = value.replaceAll(`{${name}}`, String(replacement));
  }
  return value;
}

export function translateGroupLabel(locale: Locale, label: string) {
  switch (label) {
    case "Chat":
      return t(locale, "nav.group.chat");
    case "Control":
      return t(locale, "nav.group.control");
    case "Agent":
      return t(locale, "nav.group.agent");
    case "Settings":
      return t(locale, "nav.group.settings");
    default:
      return label;
  }
}

export function applyLocaleToDocument(locale: Locale) {
  if (typeof document === "undefined") {
    return;
  }
  document.documentElement.lang = locale === "vi" ? "vi" : "en";
  document.documentElement.dataset.locale = locale;
}

function preserveWhitespace(original: string, translated: string) {
  const prefix = original.match(/^\s*/)?.[0] ?? "";
  const suffix = original.match(/\s*$/)?.[0] ?? "";
  return `${prefix}${translated}${suffix}`;
}

export function translateLiteral(locale: Locale, source: string) {
  if (locale === "en") {
    return source;
  }
  const trimmed = source.trim();
  if (!trimmed) {
    return source;
  }
  if (trimmed in exactLiteralVi) {
    return preserveWhitespace(source, exactLiteralVi[trimmed]!);
  }
  for (const rule of literalRules[locale]) {
    const match = rule.test.exec(trimmed);
    if (match) {
      return preserveWhitespace(source, rule.translate(match));
    }
  }
  return source;
}

function shouldSkipNode(node: Text) {
  const parent = node.parentElement;
  if (!parent) {
    return true;
  }
  if (parent.closest("pre, code, textarea, [data-i18n-skip='true']")) {
    return true;
  }
  if (parent.closest(".mono, .code-block")) {
    return true;
  }
  return false;
}

function localizeTextNode(node: Text, locale: Locale) {
  if (shouldSkipNode(node)) {
    return;
  }
  const current = node.nodeValue ?? "";
  const record = textNodeState.get(node) ?? { source: current, lastApplied: current };
  if (current !== record.lastApplied) {
    record.source = current;
  }
  const next = translateLiteral(locale, record.source);
  if (current !== next) {
    node.nodeValue = next;
  }
  record.lastApplied = next;
  textNodeState.set(node, record);
}

function shouldTranslateAttribute(element: Element, attribute: (typeof LOCALIZED_ATTRIBUTES)[number]) {
  if (attribute === "value") {
    return (
      element instanceof HTMLInputElement &&
      (element.type === "button" || element.type === "submit" || element.type === "reset")
    );
  }
  return true;
}

function localizeElementAttributes(element: Element, locale: Locale) {
  let recordMap = attrState.get(element);
  if (!recordMap) {
    recordMap = new Map();
    attrState.set(element, recordMap);
  }
  for (const attribute of LOCALIZED_ATTRIBUTES) {
    if (!shouldTranslateAttribute(element, attribute)) {
      continue;
    }
    const current = element.getAttribute(attribute);
    if (current == null) {
      continue;
    }
    const record = recordMap.get(attribute) ?? { source: current, lastApplied: current };
    if (current !== record.lastApplied) {
      record.source = current;
    }
    const next = translateLiteral(locale, record.source);
    if (current !== next) {
      element.setAttribute(attribute, next);
    }
    record.lastApplied = next;
    recordMap.set(attribute, record);
  }
}

export function applyDomTranslations(root: ParentNode, locale: Locale) {
  applyLocaleToDocument(locale);
  if (typeof document === "undefined") {
    return;
  }
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    localizeTextNode(node as Text, locale);
    node = walker.nextNode();
  }
  const elements =
    root instanceof Element
      ? [root, ...Array.from(root.querySelectorAll("*"))]
      : Array.from((root as DocumentFragment).querySelectorAll?.("*") ?? []);
  for (const element of elements) {
    localizeElementAttributes(element, locale);
  }
}

export function getLoaderLocale() {
  if (typeof window === "undefined") {
    return DEFAULT_LOCALE;
  }
  try {
    const raw = window.localStorage.getItem("openclaw.control.settings.v1");
    if (raw) {
      const parsed = JSON.parse(raw) as { locale?: string };
      return resolveInitialLocale(parsed.locale ?? null);
    }
  } catch {
    return resolveInitialLocale();
  }
  return resolveInitialLocale();
}

export function translateLoaderCopy(locale: Locale) {
  return {
    badge: t(locale, "loader.badge"),
    title: t(locale, "loader.title"),
    subtitle: t(locale, "loader.subtitle"),
  };
}
